import { routes } from 'constants/routes'
import CookieConsent from 'containers/CookieConsent'
import { ForceAppRefresh } from 'containers/ForceAppRefresh'
import PrivateRoute from 'containers/PrivateRoute'
import PublicOnlyRoute from 'containers/PublicOnlyRoute'
import { StyledToasts } from 'containers/Toasts'
import CoreLayout from 'layouts/CoreLayout'
import AcceptShareAccessRequestView from 'modules/shares/AcceptShareAccessRequestView'
import React from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import styled from 'styled-components'
import EmailVerifyView from 'views/EmailVerifyView'
import ModalRoot from './ModalManager/ModalRoot'
import Providers from './Providers'
import UserContext from './UserContext'
import { History } from 'history'
import { EmbedGate } from 'components/Embedding'
import { SsoRoutes } from 'modules/sso/components/SsoRoutes'
import SignInView from 'modules/user/views/SignInView'
import SignUpView from 'modules/user/views/SignUpView'
import AboutYouView from 'modules/user/views/AboutYouView'
import ForgotPasswordView from 'modules/user/views/ForgotPasswordView'
import ResetPasswordView from 'modules/user/views/ResetPasswordView'
import DevErrors from 'modules/devtools/DevErrors'
import { WorkspaceRedirectRoute } from 'modules/workspace/containers/WorkspaceRedirectRoute'
import useVisibilityTracker from 'utils/hooks/useVisibilityTracker'
import { PageLoadAnalytics } from 'modules/analytics/components/PageLoadAnalytics'
import { TelemetryConfig } from 'modules/analytics/components/TelemetryConfig'

const DevTools = React.lazy(
  () => import('modules/devtools/components/DevTools/DevTools')
)

const PageWrapper = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`

interface AppProps {
  history: History
}

const App: React.FC<AppProps> = ({ history }) => {
  useVisibilityTracker()

  return (
    <Providers history={history}>
      <ForceAppRefresh />
      <TelemetryConfig />
      <PageLoadAnalytics />
      {(process.env.REACT_APP_ENV === 'dev' ||
        process.env.REACT_APP_ENV === 'test') && (
        <>
          {/**
           * Warnings that may appear before this render
           * (e.g. in the <Providers />), will not be reported by showing a
           * toast, however, that is an acceptable tradeoff for us */}
          <DevErrors />
          <React.Suspense fallback={null}>
            <DevTools />
          </React.Suspense>
        </>
      )}

      <EmbedGate>
        <PageWrapper>
          <ModalRoot />
          <Switch>
            <PublicOnlyRoute
              path={routes.SIGN_IN.create({})}
              component={SignInView}
              exact
            />
            <PublicOnlyRoute
              path={routes.SIGN_UP.create({})}
              component={SignUpView}
              exact
            />
            <PrivateRoute
              path={routes.ABOUT_YOU.create({})}
              component={AboutYouView}
              exact
            />
            <PublicOnlyRoute
              path={routes.FORGOT_PASSWORD.create({})}
              redirectPath={routes.PERSONAL_SETTINGS.create({})}
              component={ForgotPasswordView}
              exact
            />
            <Route
              path={routes.RESET_PASSWORD.create({})}
              component={ResetPasswordView}
              exact
            />

            {/* enter from SSO */}
            <SsoRoutes
              exact
              path={[
                routes.SSO_SIGN_IN.create({}),
                routes.SSO_ERROR.template(),
                routes.SSO_SUCCESSFUL_SIGN_IN.template(),
                routes.SSO_LINK_ACCOUNT.template(),
                routes.SSO_TOS.template(),
              ]}
            />

            {/* enter from email */}
            <Route
              path={routes.ACCEPT.template()}
              component={EmailVerifyView}
            />
            <Route
              path={routes.CONFIRM.template()}
              component={EmailVerifyView}
            />
            <PrivateRoute
              path={routes.SHARE_ACCEPT_ACCESS_REQUEST.template()}
              component={AcceptShareAccessRequestView}
            />

            {/**
             * www.sketch.cloud/teams/... routes were changed to www.sketch.com/team/...
             * For backwards compatibility these routes redirect users to /team/ route
             * see https://github.com/sketch-hq/Cloud/issues/2957
             */}
            <Route
              path={routes.TEAMS_LEGACY.create({})}
              render={({ location: { search, state, pathname } }) => (
                <Redirect
                  to={{
                    search,
                    state,
                    pathname: pathname.replace('teams', 'team'),
                  }}
                  push={false}
                />
              )}
            />

            <Route
              path={[
                routes.ROOT_LEGACY.create({}),
                routes.ENTRY.create({}),

                // Workspace agnostic routes
                routes.WORKSPACE_AGNOSTIC_SETTINGS.create({}),
                routes.WORKSPACE_AGNOSTIC_SETTINGS_GENERAL.create({}),
                routes.WORKSPACE_AGNOSTIC_SETTINGS_BILLING.create({}),
                routes.WORKSPACE_AGNOSTIC_SETTINGS_SSO.create({}),
              ]}
              component={WorkspaceRedirectRoute}
              exact
            />

            <Route component={CoreLayout} />
          </Switch>
          <CookieConsent />
          <UserContext />
          <StyledToasts />
        </PageWrapper>
      </EmbedGate>
    </Providers>
  )
}

export default App
